<template>
    <div class="position-relative">
        <!-- Header and Filter Section -->
        <b-card class="mt-4">
            <b-row class="align-items-end">
                <!-- Programme Dropdown -->
                <b-col md="2">
                    <b-form-group label="Choose Programme:" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedProgrammeId" :options="programmeOptions" class="form-control-sm"
                            @change="onProgrammeChange" />
                    </b-form-group>
                </b-col>

                <!-- Subgroup Dropdown -->
                <b-col md="2">
                    <b-form-group :label="`Choose ${subgroupLabel}:`" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedSubgroupId" :options="subgroupOptions" class="form-control-sm"
                            @change="onSubgroupChange" />
                    </b-form-group>
                </b-col>

                <!-- Category Dropdown -->
                <b-col md="3">
                    <b-form-group label="Choose Structure:" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedCategoryId" :options="categoryOptions" class="form-control-sm"
                            @change="onCategoryChange" />
                    </b-form-group>
                </b-col>

                <!-- Bill Dropdown -->
                <b-col md="3">
                    <b-form-group label="Choose Bill:" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedBillId" :options="filteredBillOptions" class="form-control-sm" />
                    </b-form-group>
                </b-col>

                <!-- Go Button -->
                <b-col md="2">
                    <b-button variant="primary" size="sm" class="w-100 font-weight-medium" @click="fetchStudentBillMappings"
                        :disabled="!selectedProgrammeId || !selectedSubgroupId || !selectedCategoryId || !selectedBillId">
                        Go
                    </b-button>
                </b-col>
            </b-row>
        </b-card>

        <!-- Student Table -->
        <div v-if="studentList.length">
            <div v-if="formError" class="alert alert-danger mb-3">{{ formError }}</div>

            <table class="table table-striped table-bordered">
                <thead class="thead-dark">
                    <tr>
                        <th>SI. No.</th>
                        <th>Bill Number</th>
                        <th>Student Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Amount (₹)</th>
                        <th>Adjustments</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(student, index) in studentList" :key="student.bill_id">
                        <td>{{ index + 1 }}</td>
                        <td>{{ student.bill_number }}</td>
                        <td>{{ student.name }}</td>
                        <td>{{ student.email }}</td>
                        <td>{{ student.mobile }}</td>
                        <td class="d-flex align-items-center">
                            <b-form-input :value="student.debit_amount" type="number" class="flex-grow-1" disabled />
                            <div v-if="student.is_paid" class="ms-2 tooltip-wrapper">
                                <b-icon icon="lock-fill" variant="danger"></b-icon>
                                <div class="custom-tooltip">
                                    <span>Payment Received</span>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div v-if="student.adjustments.length">
                                <div v-for="adjustment in student.adjustments" :key="adjustment.transaction_id"
                                    class="tooltip-wrapper d-inline-block">
                                    <b-badge :variant="adjustment.debit_amount < 0 ? 'success' : 'danger'"
                                        class="mr-1 mb-1">
                                        {{ adjustment.debit_amount > 0 ? '+' : '' }}₹{{ adjustment.debit_amount }}
                                    </b-badge>
                                    <div class="custom-tooltip">{{ adjustment.note }}</div>
                                </div>
                            </div>
                            <div v-else-if="!student.is_paid && student.savedDebitAmount"
                                class="d-flex align-items-center">
                                <b-form-input v-model.number="student.adjustmentAmount" type="number"
                                    placeholder="Amount" class="mr-1" style="max-width: 100px;" />
                                <b-form-input v-model.trim="student.adjustmentNote" type="text"
                                    placeholder="Reason" class="mr-1" style="max-width: 140px;" />
                                <b-button size="sm" variant="info" @click="saveAdjustment(student)">
                                    <i class="fas fa-check"></i>
                                </b-button>
                            </div>
                            <span v-else-if="student.is_paid" class="text-muted small">
                                Payment already received - adjustments are locked.
                            </span>
                            <span v-else class="text-muted small">
                                Save an amount for this student first to add an adjustment.
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-else>
            <div v-if="loading" class="d-flex justify-content-center mt-3">
                <span>Loading...</span>
            </div>
            <div v-else-if="submitted && studentList.length === 0" class="d-flex justify-content-center mt-3">
                <span class="text-danger">No students found for this bill in the selected class.</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            submitted: false,
            loading: false,

            selectedProgrammeId: null,
            selectedSubgroupId: null,
            selectedCategoryId: null,
            selectedBillId: null,

            subgroupLabel: '',

            programmeOptions: [{ value: null, text: 'Select an option' }],
            subgroupOptions: [{ value: null, text: 'Select an option' }],
            categoryOptions: [{ value: null, text: 'Select an option' }],
            bills: [],

            studentList: [],
            formError: '',
        };
    },

    computed: {
        filteredBillOptions() {
            if (!this.selectedCategoryId) {
                return [{ value: null, text: 'Select a structure first' }];
            }
            const filtered = this.bills.filter((bill) => bill.category_id === this.selectedCategoryId);
            return [
                { value: null, text: 'Select an option' },
                ...filtered.map((bill) => ({
                    value: bill.id,
                    text: `${bill.name} (${bill.component_name})`,
                })),
            ];
        },
    },

    async created() {
        this.subgroupLabel = this.$store.getters.getCustomNamings?.userSubgroup?.custom_name || 'Usersubgroup';
        await this.loadProgrammes();
        await this.loadCategories();
        await this.loadBills();
    },

    methods: {
        onProgrammeChange() {
            this.selectedSubgroupId = null;
            this.studentList = [];
            this.submitted = false;
            this.loadSubgroups();
        },

        onSubgroupChange() {
            this.studentList = [];
            this.submitted = false;
        },

        onCategoryChange() {
            this.selectedBillId = null;
            this.studentList = [];
            this.submitted = false;
        },

        async loadProgrammes() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups?allusergroups=1`;
            const response = await this.$axios.get(url);
            this.programmeOptions = [
                { value: null, text: 'Select an option' },
                ...response.data.map((programme) => ({
                    value: programme.id,
                    text: `${programme.name} (${programme.code}) ${programme.scheme || ''}`,
                })),
            ];
        },

        async getDefaultAcademicYear() {
            // $root.default_academic_year is only ever populated as a side effect of
            // visiting the Staff List / Set Academic Year pages, not during app
            // bootstrap - fetch it directly instead of trusting it's already set,
            // otherwise this silently queries academic_year=null and returns nothing.
            if (this.$root.default_academic_year[0]) return this.$root.default_academic_year[0];

            const url = `${this.$store.getters.getAPIKey.mainAPI}/user/setting/default_academic_year`;
            const response = await this.$axios.get(url);
            this.$root.default_academic_year[0] = `${response.data}`;
            return this.$root.default_academic_year[0];
        },

        async loadSubgroups() {
            try {
                const academicYear = await this.getDefaultAcademicYear();
                const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups/${this.selectedProgrammeId}/usersubgroups?academic_year=${academicYear}`;
                const response = await this.$axios.get(url);
                this.subgroupOptions = [
                    { value: null, text: 'Select an option' },
                    ...response.data
                        .filter((subgroup) => subgroup.type === null)
                        .map((subgroup) => ({
                            value: subgroup.id,
                            text: `${subgroup.name} (${subgroup.code})`
                        }))
                ];
            } catch (error) {
                error;
                this.subgroupOptions = [{ value: null, text: 'Error loading options' }];
            }
        },

        async loadCategories() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/feecategories`;
            const response = await this.$axios.get(url);
            this.categoryOptions = [
                { value: null, text: 'Select an option' },
                ...response.data.map((category) => ({
                    value: category.id,
                    text: category.name,
                })),
            ];
        },

        async loadBills() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/feebills`;
            const response = await this.$axios.get(url);
            this.bills = response.data;
        },

        async fetchStudentBillMappings() {
            this.loading = true;
            this.formError = '';
            try {
                const subgroupUrl = `${this.$store.getters.getAPIKey.mainAPI}/usersubgroup/${this.selectedSubgroupId}/users`;
                const billUrl = `${this.$store.getters.getAPIKey.mainAPI}/feebills/${this.selectedBillId}/transactions`;

                const [subgroupResponse, billResponse] = await Promise.all([
                    this.$axios.get(subgroupUrl),
                    this.$axios.get(billUrl),
                ]);

                const subgroupStudentIds = subgroupResponse.data
                    .filter((user) => user.pivot.usersubgrouprole_id === 2)
                    .map((user) => user.pivot.institution_user_id);

                const byBill = {};
                billResponse.data.transactions
                    .filter((transaction) => subgroupStudentIds.includes(transaction.institution_user_id))
                    .forEach((transaction) => {
                        if (!byBill[transaction.bill_id]) {
                            byBill[transaction.bill_id] = {
                                adjustments: [], adjustmentAmount: null, adjustmentNote: '', is_paid: false,
                            };
                        }
                        const entry = byBill[transaction.bill_id];

                        if (transaction.is_adjustment) {
                            entry.adjustments.push(transaction);
                            return;
                        }

                        // A paid bill has TWO non-adjustment rows: the original debit-only
                        // row and a separate credit-only row created at payment time - both
                        // share this same bill_id, and the order they arrive in isn't
                        // guaranteed. Merge field-by-field instead of Object.assign-ing
                        // whichever comes last, or one row's nulls blank out the other's data.
                        entry.bill_id = transaction.bill_id;
                        entry.bill_number = transaction.bill_number;
                        entry.institution_user_id = transaction.institution_user_id;
                        entry.name = transaction.name;
                        entry.email = transaction.email;
                        entry.mobile = transaction.mobile;

                        if (transaction.debit_amount !== null && transaction.debit_amount !== undefined) {
                            entry.transaction_id = transaction.transaction_id;
                            entry.debit_amount = parseFloat(transaction.debit_amount);
                            // Snapshot of what's actually saved in the DB right now -
                            // separate from debit_amount, which changes live as the
                            // staff member types, before it's ever submitted via Update.
                            entry.savedDebitAmount = parseFloat(transaction.debit_amount);
                        }

                        if (parseFloat(transaction.credit_amount) > 0) {
                            entry.is_paid = true;
                            entry.credit_amount = transaction.credit_amount;
                        }
                    });

                this.studentList = Object.values(byBill);
            } catch (error) {
                error;
                this.formError = 'Failed to load students for this bill. Please try again.';
            } finally {
                this.submitted = true;
                this.loading = false;
            }
        },

        async saveAdjustment(student) {
            if (student.is_paid) {
                this.$toast.error('Payment already received - adjustments are locked.');
                return;
            }

            if (!student.savedDebitAmount) {
                this.$toast.error('Please save an amount for this student before adding an adjustment.');
                return;
            }

            if (!student.adjustmentAmount || !student.adjustmentNote) {
                this.$toast.error('Amount and reason are both required.');
                return;
            }

            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/feebills/${student.bill_id}/adjustments`;
                await this.$axios.post(url, {
                    amount: student.adjustmentAmount,
                    note: student.adjustmentNote,
                });

                this.$toast.success('Adjustment added successfully.', {
                    position: 'top',
                    duration: 4000,
                });

                await this.fetchStudentBillMappings();
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to add adjustment. Please try again.');
            }
        },
    },
};
</script>

<style scoped>
.required-label::after {
    content: '*';
    color: red;
    margin-left: 4px;
}

.tooltip-wrapper {
    position: relative;
    display: inline-block;
    cursor: pointer;
}

.custom-tooltip {
    visibility: hidden;
    background-color: #000;
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    position: absolute;
    z-index: 999;
    top: 100%;
    left: 0;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.3s;
    margin-top: 5px;
    font-size: 13px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
}

.tooltip-wrapper:hover .custom-tooltip {
    visibility: visible;
    opacity: 1;
}
</style>
